import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { fetchEmailFormats, fetchAllEmailTypes } from "../Services/APIService";
import {
  EmailType,
  EmailFormat,
  EmailFormatFormData,
} from "../types/emailFormat.types";

export const useEmailFormatTable = () => {
  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Email Format Data State
  const [emailFormats, setEmailFormats] = useState<EmailFormat[]>([]);
  const [emailTypes, setEmailTypes] = useState<EmailType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editEmailFormatData, setEditEmailFormatData] =
    useState<EmailFormat | null>(null);

  // Table State
  const [selectedRows, setSelectedRows] = useState<EmailFormat[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const fetchEmailFormatData = async (): Promise<void> => {
    try {
      setLoading(true);
      const formats: EmailFormat[] = await fetchEmailFormats();
      const types = await fetchAllEmailTypes();
      setEmailFormats(formats);
      setEmailTypes(types);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  const handleEdit = (emailFormat: EmailFormat) => {
    setEditEmailFormatData(emailFormat);
    setIsEditModalOpen(true);
  };

  const handleRowSelected = (state: { selectedRows: EmailFormat[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const filteredEmailFormats = emailFormats.filter(
    (format) =>
      format.subject.toLowerCase().includes(searchText.toLowerCase()) ||
      format.typeName.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle location state and menu logic
  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const emailFormatDataUpdated = location.state?.emailFormatDataUpdated;

        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        if (emailFormatDataUpdated) {
          toast.success("Email Format updated successfully");
          fetchEmailFormatData();
        }

        if (subMenuId == 3) {
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error("Error handling menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // Initial email formats fetch
  useEffect(() => {
    fetchEmailFormatData();
  }, []);

  return {
    isSidebarCollapsed,
    toggleSidebar,
    isLoading,
    emailFormats,
    emailTypes,
    loading,
    error,
    searchText,
    setSearchText,
    filteredEmailFormats,
    fetchEmailFormatData,
    isModalOpen,
    setIsModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    editEmailFormatData,
    setEditEmailFormatData,
    selectedRows,
    toggleCleared,
    handleRowSelected,
    handleEdit,
  };
};
